import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  SETTINGS_NAV_GROUP_LABEL_KEYS,
  SETTINGS_NAV_GROUP_ORDER,
  getVisibleSettingsNavItems,
  type SettingsNavGroup,
  type SettingsNavItem,
} from '@/components/settings/settings-navigation.config';

function groupNavItems(items: SettingsNavItem[]): Partial<Record<SettingsNavGroup, SettingsNavItem[]>> {
  return items.reduce<Partial<Record<SettingsNavGroup, SettingsNavItem[]>>>((groups, item) => {
    const groupItems = groups[item.group] ?? [];
    groupItems.push(item);
    groups[item.group] = groupItems;
    return groups;
  }, {});
}

export function SettingsOverviewPage() {
  const { t } = useTranslation();
  const { user } = useAuth({ middleware: 'auth', tmp: 'SettingsOverviewPage' });
  const visibleItems = getVisibleSettingsNavItems(user);
  const groupedItems = groupNavItems(visibleItems);

  return (
    <div className="container mx-auto max-w-4xl space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-semibold">{t('settings.overview.title')}</h1>
        <p className="mt-2 text-muted-foreground">{t('settings.overview.description')}</p>
      </div>

      {SETTINGS_NAV_GROUP_ORDER.map((group) => {
        const items = groupedItems[group];
        if (!items?.length) {
          return null;
        }

        return (
          <section key={group} aria-labelledby={`settings-group-${group}`}>
            <h2
              id={`settings-group-${group}`}
              className="mb-3 font-label text-sm font-medium uppercase tracking-wide text-muted-foreground"
            >
              {t(SETTINGS_NAV_GROUP_LABEL_KEYS[group])}
            </h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {items.map((item) => (
                <li key={item.id}>
                  <Link to={item.to} className="block h-full rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    <Card className="h-full transition-colors hover:border-brand-secondary/40 hover:bg-muted/30">
                      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
                        <div className="min-w-0 space-y-1">
                          <CardTitle className="text-base">{t(item.labelKey)}</CardTitle>
                          <CardDescription>{t(item.descriptionKey)}</CardDescription>
                        </div>
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      </CardHeader>
                    </Card>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
